import { isAfter, isBeforeOrEqual, getNow, getPreviousDay, isBefore, printDate } from '@kathondvla/sri-client/date-utils';
import {
  UITTREDEND,
  NASCHOLINGSVERANTWOORDELIJKE,
  KATHOLIEK_ONDERWIJS_VLAANDEREN,
  LEERPLANCOMMISSIE,
  VERGADERGROEP
} from 'ReduxLoop/constants';
import { settings } from 'Js/config/settings';

const stepsConfig = {
  1: {
    subtitle: 'Bevestig dat je een nieuw team nodig hebt',
    continueText: 'Ja, ik wil een nieuw team creëren',
  },
  2: {
    subtitle: 'Vul alle velden in',
    continueText: 'Ga verder',
  },
  3: {
    subtitle: 'Voltooi de registratie',
    continueText: 'Verstuur en ga verder',
  },
  4: {
    subtitle: '',
    continueText: 'Beheer je team',
  },
};

const getReferenceDate = (ou) => {
  if (isAfter(ou.startDate, getNow())) {
    return ou.startDate;
  }
  if (isBefore(ou.endDate, getNow())) {
    return getPreviousDay(ou.endDate);
  }
  return getNow();
}

const getDuplicateSuggestion = (state) => {
  if (!state.createTeamWizard.duplicateSuggestion) {
    return null;
  }
  const referenceDate = getReferenceDate(state.createTeamWizard.duplicateSuggestion);
  const duplicateSuggestionName = state.createTeamWizard.duplicateSuggestion.names
    .find((name) => name.type === 'OFFICIAL' && isBeforeOrEqual(name.startDate, referenceDate) && isAfter(name.endDate, referenceDate));
  const duplicateSuggestionShortName = state.createTeamWizard.duplicateSuggestion.names
    .find((name) => name.type === 'SHORT' && isBeforeOrEqual(name.startDate, referenceDate) && isAfter(name.endDate, referenceDate));
  return {
    permalink: state.createTeamWizard.duplicateSuggestion.$$meta.permalink,
    key: state.createTeamWizard.duplicateSuggestion.key,
    name: duplicateSuggestionName?.value,
    shortName: duplicateSuggestionShortName?.value,
    description: state.createTeamWizard.duplicateSuggestion.description,
    type: state.createTeamWizard.duplicateSuggestion.type,
    startsOn: isAfter(state.createTeamWizard.duplicateSuggestion.startDate, getNow()) ? printDate(state.createTeamWizard.duplicateSuggestion.startDate) : null,
    abolishedOn: !isAfter(state.createTeamWizard.duplicateSuggestion.endDate, getNow()) ? printDate(state.createTeamWizard.duplicateSuggestion.endDate) : null,
    isJoinable: [VERGADERGROEP, LEERPLANCOMMISSIE].some(
      (type) => type === state.createTeamWizard.duplicateSuggestion.type
    ) && isAfter(state.createTeamWizard.duplicateSuggestion.endDate, getNow()),
    presidents: state.createTeamWizard.duplicateVoorziterResponsibilities.map((resp) => ({
      permalink: resp.person.href,
      fullName: `${resp.person.$$expanded.firstName} ${resp.person.$$expanded.lastName}`,
      responsibilityHref: resp.$$meta.permalink,
    })),
  };
};

const getPathToKathOndVla = (ouHref, state) => {
  const path = [ouHref];
  let currentOuHref = ouHref;
  let count = 0;
  while (currentOuHref !== KATHOLIEK_ONDERWIJS_VLAANDEREN) {
    const parentRel = state.userState.dienstParentRelations.find((rel) => rel.from.href === currentOuHref);
    path.push(parentRel.to.href);
    currentOuHref = parentRel.to.href;
    count++;
    if (count > 10) {
      throw new Error(`Person has responsibility in ou ${ouHref} of type DIENST but this is not an ancestor of KathOndVla!`);
    }
  }
  return path;
}

const getResponsibilitiesInKathOndVla = (state) => {
  const respPaths = state.userState.responsibilities
    .filter((resp) => {
      const ou = state.userState.ous[resp.organisationalUnit.href];
      return ou.type === 'DIENST'
        && resp.position
        && resp.position.href !== NASCHOLINGSVERANTWOORDELIJKE
        && !(resp.position.href === UITTREDEND && resp.endDate === '2026-07-01');
    })
    .map((resp) => getPathToKathOndVla(resp.organisationalUnit.href, state));

  if (respPaths.length > 1) {
    console.warn('Person has multiple responsibilities in different branches of KathOndVla. We will take the shortest path.', respPaths);
  }
  
  return respPaths.reduce((acc, path) => {
    return acc.length < path.length ? acc : path;
  });
}

export const getMainResponsibilityInfo = (state) => {
  if (!state.userState.responsibilities || !state.userState.dienstParentRelations) {
    return null;
  }
  const mainPathToKathOndVla = getResponsibilitiesInKathOndVla(state);
  if (mainPathToKathOndVla.length === 0) {
    return null;
  }

  const afdelingHref = mainPathToKathOndVla[mainPathToKathOndVla.length - 2];
  const afdelingConfig = Object.entries(settings.kathOndVlaAfdelingen)
    .map(([key, value]) => ({ key, ...value }))
    .find(({ href }) => href === afdelingHref);

  if (Object.values(settings.congregaties).some((href) => href === afdelingHref)) {
    return {
      mainPathToKathOndVla,
      afdeling: null,
      isCongregatie: true,
      regionOrCongregatieOrVdHref: mainPathToKathOndVla[mainPathToKathOndVla.length - 2],
    }
  }
  if (afdelingConfig.key === 'DIENSTVERLENING') {
    // check if he is directly on afdeling dienstverlening, not in a lower level
    if (mainPathToKathOndVla.length === 2) {
      return {
        mainPathToKathOndVla,
        afdeling: afdelingConfig,
      }
    }
    // check if this is in a region or a vicariale dienst
    const level3Href = mainPathToKathOndVla[mainPathToKathOndVla.length - 3];
    if (Object.values(settings.vicarialeDiensten).some((href) => href === level3Href)) {
      return {
        mainPathToKathOndVla,
        afdeling: null,
        isVicarialeDienst: true,
        regionOrCongregatieOrVdHref: level3Href,
      }
    }
    const level3Ou = state.userState.ous[level3Href];
    if (level3Ou.$$displayName.toLowerCase().startsWith('pb regio')) {
      return {
        mainPathToKathOndVla,
        afdeling: null,
        isRegion: true,
        regionOrCongregatieOrVdHref: level3Href,
      }
    }
  }

  return {
    mainPathToKathOndVla,
    afdeling: afdelingConfig,
  };
}

export const getCreateTeamWizardModel = (state) => {
  return {
    currentStep: state.createTeamWizard.currentStep,
    currentStepConfig: stepsConfig[state.createTeamWizard.currentStep],
    possibleTypes: state.createTeamWizard.possibleTypes,
    shortNamePattern: state.createTeamWizard.shortNamePattern,
    code: state.createTeamWizard.code,
    submitAttempted: state.createTeamWizard.submitAttempted,
    persistingNewTeam: state.createTeamWizard.persistingNewTeam,
    form: {
      name: state.createTeamWizard.name,
      shortName: state.createTeamWizard.shortName,
      description: state.createTeamWizard.description,
      type: state.createTeamWizard.selectedType,
      startDate: state.createTeamWizard.startDate,
    },
    formErrors: {
      descriptionErrors: state.createTeamWizard.descriptionErrors,
      nameErrors: state.createTeamWizard.nameErrors,
      shortNameErrors: state.createTeamWizard.shortNameErrors,
      nameWarnings: state.createTeamWizard.nameWarnings,
    },
    duplicateSuggestion: getDuplicateSuggestion(state),
    duplicateErrorType:
      (state.createTeamWizard.nameErrors && state.createTeamWizard.nameErrors.length > 0) ||
      (state.createTeamWizard.shortNameErrors && state.createTeamWizard.shortNameErrors.length > 0)
        ? 'ERROR'
        : 'WARNING',
    notifyDepartmentFailed: state.createTeamWizard.notifyDepartmentFailed,
  };
};
